import { useState } from 'react';
import { usePartnerDashboard } from '../hooks/usePartnerDashboard';
import { usePartnerProfile } from '../hooks/usePartnerPayouts';
import { Button, Card, CardContent, CardHeader, CardTitle, CardDescription } from '@medikei/ui';
import {
  Link2,
  Copy,
  Check,
  Ticket,
  Video,
  Home,
  Share2,
  MessageCircle,
  Mail,
  QrCode,
  Zap,
  Building2,
  ExternalLink,
} from 'lucide-react';
import { shareOnWhatsApp, shareOnInstagram, shareOnEmail, copyToClipboard } from '@/lib/share';

function formatCurrency(value: number): string {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(value);
}

export function LinksPage() {
  const { data: dashboard, isLoading: isDashboardLoading } = usePartnerDashboard();
  const { data: profile, isLoading: isProfileLoading } = usePartnerProfile();

  const [copiedLink, setCopiedLink] = useState(false);
  const [copiedCoupon, setCopiedCoupon] = useState(false);
  const [copiedKey, setCopiedKey] = useState<string | null>(null);
  const [showQR, setShowQR] = useState(false);

  const partnerHash = profile?.hash || dashboard?.partner?.hash || '';

  const getFullBaseUrl = () => {
    if (typeof window !== 'undefined' && window.location.origin) {
      if (window.location.origin.includes('localhost')) {
        return window.location.origin;
      }
      return 'https://www.medikei.com.br';
    }
    return 'https://www.medikei.com.br';
  };

  const baseUrl = getFullBaseUrl();
  const affiliateLink = `${baseUrl}/p/${partnerHash}`;
  const couponCode = `MEDIKEI${partnerHash.slice(0, 6).toUpperCase()}`;

  // 4 Links Diretos Oficiais por Serviço com Parâmetro de Rastreamento Ativo
  const expressaWebLink = `${baseUrl}/expressa?partner=${partnerHash}`;
  const telemedicinaWebLink = `${baseUrl}/medicos?modality=TELEMEDICINA&partner=${partnerHash}`;
  const presencialWebLink = `${baseUrl}/medicos?modality=PRESENCIAL&partner=${partnerHash}`;
  const domiciliarWebLink = `${baseUrl}/medicos?modality=DOMICILIAR&partner=${partnerHash}`;

  const isLoading = isDashboardLoading || isProfileLoading;

  const handleCopyLink = async () => {
    copyToClipboard(affiliateLink);
    setCopiedLink(true);
    setTimeout(() => setCopiedLink(false), 2000);
  };

  const handleCopyCoupon = async () => {
    copyToClipboard(couponCode);
    setCopiedCoupon(true);
    setTimeout(() => setCopiedCoupon(false), 2000);
  };

  const handleCopyDirectLink = (key: string, link: string) => {
    copyToClipboard(link);
    setCopiedKey(key);
    setTimeout(() => setCopiedKey(null), 2000);
  };

  const handleShareWhatsApp = () => {
    shareOnWhatsApp('Olá! Use meu link para agendar consultas no Medikei:', affiliateLink);
  };

  const handleShareInstagram = () => {
    shareOnInstagram('Use meu link Medikei para agendar sua consulta:', affiliateLink);
  };

  const handleShareEmail = () => {
    shareOnEmail('Consulta de Saúde - Medikei', `Olá!\n\nUse meu link para agendar consultas no Medikei:\n${affiliateLink}\n\nAté!`);
  };

  const handleShareServiceWhatsApp = (text: string, link: string) => {
    shareOnWhatsApp(text, link);
  };

  const directServices = [
    {
      id: 'expressa',
      title: 'Plantão 24h / Consulta Expressa',
      badge: 'Imediato 24h',
      badgeClass: 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300 dark:border-emerald-800',
      description: 'Atendimento médico imediato por vídeo sem necessidade de agendamento.',
      icon: Zap,
      iconClass: 'bg-emerald-50 text-emerald-600 dark:bg-emerald-950/60 dark:text-emerald-400 border border-emerald-100 dark:border-emerald-900/60',
      link: expressaWebLink,
      whatsappText: 'Olá! Precisa de atendimento médico agora? Acesse o plantão 24h do Medikei e consulte online sem filas:',
    },
    {
      id: 'telemedicina',
      title: 'Telemedicina Agendada',
      badge: 'Vídeo HD Marcado',
      badgeClass: 'bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-950/40 dark:text-blue-300 dark:border-blue-800',
      description: 'Consulta online com horário marcado com especialistas na agenda médica.',
      icon: Video,
      iconClass: 'bg-blue-50 text-blue-600 dark:bg-blue-950/60 dark:text-blue-400 border border-blue-100 dark:border-blue-900/60',
      link: telemedicinaWebLink,
      whatsappText: 'Olá! Agende sua consulta online por telemedicina com especialistas no Medikei:',
    },
    {
      id: 'presencial',
      title: 'Consulta Presencial',
      badge: 'Consultório / Clínica',
      badgeClass: 'bg-purple-50 text-purple-700 border-purple-200 dark:bg-purple-950/40 dark:text-purple-300 dark:border-purple-800',
      description: 'Atendimento médico presencial em clínicas e consultórios credenciados.',
      icon: Building2,
      iconClass: 'bg-purple-50 text-purple-600 dark:bg-purple-950/60 dark:text-purple-400 border border-purple-100 dark:border-purple-900/60',
      link: presencialWebLink,
      whatsappText: 'Olá! Agende sua consulta presencial em consultórios e clínicas credenciadas no Medikei:',
    },
    {
      id: 'domiciliar',
      title: 'Consulta Domiciliar',
      badge: 'Médico em Casa',
      badgeClass: 'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-950/40 dark:text-amber-300 dark:border-amber-800',
      description: 'Atendimento médico no conforto da sua residência (home care) com comodidade.',
      icon: Home,
      iconClass: 'bg-amber-50 text-amber-600 dark:bg-amber-950/60 dark:text-amber-400 border border-amber-100 dark:border-amber-900/60',
      link: domiciliarWebLink,
      whatsappText: 'Olá! Solicite atendimento médico no conforto da sua casa com a consulta domiciliar do Medikei:',
    },
  ];

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="w-8 h-8 border-4 border-primary-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Meus Links</h1>
          <p className="text-muted-foreground mt-1">
            Compartilhe e acompanhe suas indicações
          </p>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Link2 className="w-5 h-5 text-primary" />
              Seu Link Principal de Afiliado
            </CardTitle>
            <CardDescription>
              Compartilhe este link para direcionar à plataforma e ganhar comissões em qualquer agendamento
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center gap-2 p-4 bg-neutral-50 dark:bg-neutral-800/60 rounded-xl border border-neutral-200/60 dark:border-neutral-700/60">
              <input
                type="text"
                value={affiliateLink}
                readOnly
                className="flex-1 bg-transparent text-sm font-mono outline-none text-foreground"
              />
              <Button
                variant="ghost"
                size="sm"
                onClick={handleCopyLink}
                className="gap-2 shrink-0"
              >
                {copiedLink ? (
                  <Check className="w-4 h-4 text-emerald-600" />
                ) : (
                  <Copy className="w-4 h-4" />
                )}
                {copiedLink ? 'Copiado!' : 'Copiar'}
              </Button>
            </div>

            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={() => setShowQR(!showQR)}
                className="flex-1 gap-2 rounded-xl"
              >
                <QrCode className="w-4 h-4" />
                {showQR ? 'Ocultar QR' : 'Gerar QR'}
              </Button>
              <Button
                variant="outline"
                onClick={handleShareWhatsApp}
                className="flex-1 gap-2 rounded-xl text-emerald-600 hover:text-emerald-700"
              >
                <MessageCircle className="w-4 h-4" />
                WhatsApp
              </Button>
            </div>

            {showQR && (
              <div className="flex flex-col items-center justify-center p-6 bg-white dark:bg-neutral-900 rounded-xl border border-border shadow-xs space-y-3">
                <img
                  src={`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(affiliateLink)}`}
                  alt="QR Code de Afiliado Medikei"
                  className="w-44 h-44 rounded-lg border p-2 bg-white"
                />
                <p className="text-xs text-muted-foreground text-center font-mono">
                  {affiliateLink}
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Ticket className="w-5 h-5 text-primary" />
              Seu Código de Cupom
            </CardTitle>
            <CardDescription>
              Seus contatos podem aplicar este código no checkout para vincular comissões
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center gap-2 p-4 bg-neutral-50 dark:bg-neutral-800/60 rounded-xl border border-neutral-200/60 dark:border-neutral-700/60">
              <input
                type="text"
                value={couponCode}
                readOnly
                className="flex-1 bg-transparent text-lg font-bold font-mono outline-none uppercase text-foreground"
              />
              <Button
                variant="ghost"
                size="sm"
                onClick={handleCopyCoupon}
                className="gap-2 shrink-0"
              >
                {copiedCoupon ? (
                  <Check className="w-4 h-4 text-emerald-600" />
                ) : (
                  <Copy className="w-4 h-4" />
                )}
                {copiedCoupon ? 'Copiado!' : 'Copiar'}
              </Button>
            </div>
            <p className="text-sm text-muted-foreground">
              Compartilhe este código com seus contatos. Eles podem usá-lo ao agendar consultas ou se cadastrar.
            </p>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Compartilhar</CardTitle>
          <CardDescription>
            Envie seu link rapidamente através dos seus canais favoritos
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Button
              variant="outline"
              onClick={handleShareWhatsApp}
              className="h-20 flex-col gap-2 rounded-2xl hover:border-emerald-300"
            >
              <MessageCircle className="w-6 h-6 text-green-600" />
              <span className="text-xs font-semibold">WhatsApp</span>
            </Button>
            <Button
              variant="outline"
              onClick={handleShareInstagram}
              className="h-20 flex-col gap-2 rounded-2xl hover:border-pink-300"
            >
              <Share2 className="w-6 h-6 text-pink-600" />
              <span className="text-xs font-semibold">Instagram</span>
            </Button>
            <Button
              variant="outline"
              onClick={handleCopyLink}
              className="h-20 flex-col gap-2 rounded-2xl hover:border-primary/40"
            >
              {copiedLink ? <Check className="w-6 h-6 text-emerald-600" /> : <Copy className="w-6 h-6 text-neutral-600" />}
              <span className="text-xs font-semibold">{copiedLink ? 'Copiado!' : 'Copiar Link'}</span>
            </Button>
            <Button
              variant="outline"
              onClick={handleShareEmail}
              className="h-20 flex-col gap-2 rounded-2xl hover:border-blue-300"
            >
              <Mail className="w-6 h-6 text-blue-600" />
              <span className="text-xs font-semibold">E-mail</span>
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* 4 Links Diretos por Modalidade de Atendimento */}
      <Card>
        <CardHeader>
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
            <div>
              <CardTitle>Links Diretos por Serviço</CardTitle>
              <CardDescription>
                Links diretos para indicar cada modalidade específica com seu código de parceiro e comissão ativa
              </CardDescription>
            </div>
            <span className="text-xs font-semibold text-primary px-2.5 py-1 bg-primary/10 rounded-full self-start sm:self-auto">
              4 Modalidades Disponíveis
            </span>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2">
            {directServices.map((srv) => {
              const IconComp = srv.icon;
              const isCopied = copiedKey === srv.id;

              return (
                <div
                  key={srv.id}
                  className="p-4 bg-neutral-50 dark:bg-neutral-800/50 rounded-2xl border border-neutral-200/70 dark:border-neutral-700/60 space-y-3.5 hover:shadow-xs transition-shadow"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${srv.iconClass}`}>
                        <IconComp className="w-5 h-5" />
                      </div>
                      <div>
                        <h4 className="font-bold text-foreground text-sm">{srv.title}</h4>
                        <p className="text-xs text-muted-foreground mt-0.5">{srv.description}</p>
                      </div>
                    </div>
                    <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full border shrink-0 ${srv.badgeClass}`}>
                      {srv.badge}
                    </span>
                  </div>

                  {/* Input do link com ações */}
                  <div className="flex items-center gap-2">
                    <input
                      type="text"
                      value={srv.link}
                      readOnly
                      className="flex-1 bg-white dark:bg-neutral-900 px-3 py-2 rounded-xl text-xs font-mono border border-border text-foreground outline-none select-all"
                    />
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleCopyDirectLink(srv.id, srv.link)}
                      className="shrink-0 gap-1 rounded-xl text-xs"
                      title="Copiar Link"
                    >
                      {isCopied ? (
                        <>
                          <Check className="w-3.5 h-3.5 text-emerald-600" />
                          <span className="text-emerald-600 font-bold">Copiado!</span>
                        </>
                      ) : (
                        <>
                          <Copy className="w-3.5 h-3.5" />
                          <span>Copiar</span>
                        </>
                      )}
                    </Button>
                    <a
                      href={srv.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center justify-center p-2 rounded-xl border border-border hover:bg-neutral-100 dark:hover:bg-neutral-800 text-muted-foreground hover:text-foreground transition-colors shrink-0"
                      title="Testar e abrir em nova aba"
                    >
                      <ExternalLink className="w-3.5 h-3.5" />
                    </a>
                    <button
                      type="button"
                      onClick={() => handleShareServiceWhatsApp(srv.whatsappText, srv.link)}
                      className="inline-flex items-center justify-center p-2 rounded-xl border border-emerald-200 dark:border-emerald-800 bg-emerald-50 dark:bg-emerald-950/40 text-emerald-600 hover:bg-emerald-100 transition-colors shrink-0"
                      title="Compartilhar no WhatsApp"
                    >
                      <MessageCircle className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {dashboard && (
        <Card>
          <CardHeader>
            <CardTitle>Estatísticas</CardTitle>
            <CardDescription>
              Seu desempenho como afiliado
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="text-center p-4 bg-neutral-50 rounded-xl">
                <p className="text-2xl font-bold text-primary-600">
                  {dashboard.stats?.totalClicks || 0}
                </p>
                <p className="text-sm text-muted-foreground">Cliques</p>
              </div>
              <div className="text-center p-4 bg-neutral-50 rounded-xl">
                <p className="text-2xl font-bold text-emerald-600">
                  {dashboard.stats?.totalConversions || 0}
                </p>
                <p className="text-sm text-muted-foreground">Conversões</p>
              </div>
              <div className="text-center p-4 bg-neutral-50 rounded-xl">
                <p className="text-2xl font-bold text-amber-600">
                  {formatCurrency(dashboard.balance?.pending || 0)}
                </p>
                <p className="text-sm text-muted-foreground">Comissão Pendente</p>
              </div>
              <div className="text-center p-4 bg-neutral-50 rounded-xl">
                <p className="text-2xl font-bold text-emerald-600">
                  {formatCurrency(dashboard.balance?.available || 0)}
                </p>
                <p className="text-sm text-muted-foreground">Disponível</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
